import { formatRecordDate } from "@taunggyi-plus/design-system";
import { RecordFormDialog } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import { useAuth } from "@taunggyi-plus/auth";
import {
  Card,
  DetailCard,
  ManagementListToolbar,
  PaginatedList,
  StatusPill,
} from "@taunggyi-plus/design-system";
import type { Locale } from "@taunggyi-plus/domain";

type PartnerKind = "hotel" | "merchant" | "delivery_fleet" | "taxi_group";
type ApplicationStatus = "pending" | "approved" | "rejected";
interface Application {
  id: string;
  kind: PartnerKind;
  business_name: string;
  contact_name: string;
  contact_phone: string;
  address: string;
  applicant_note: string;
  status: ApplicationStatus;
  review_note: string;
  created_at: string;
}

const words = {
  en: {
    apply: "Partner application",
    applyTitle: "Register your business",
    applyHelp:
      "Submit once. TaungGyi Link will verify the details before activating your workspace.",
    business: "Business name",
    contact: "Contact person",
    phone: "Phone",
    address: "Business address",
    note: "Additional information (optional)",
    submit: "Submit for review",
    sending: "Submitting…",
    pending: "Pending review",
    approved: "Approved",
    rejected: "Rejected",
    statusHelp: "Your application status",
    newApplication: "Submit a new application",
    admin: "Partner approvals",
    adminTitle: "Manual approval queue",
    adminHelp: "Verify each business and phone number before approval. Every decision is audited.",
    signIn: "Administrator sign in",
    email: "Email",
    password: "Password",
    wait: "Please wait…",
    accessDenied: "This account does not have administrator access.",
    empty: "No pending partner applications.",
    approve: "Approve and activate",
    reject: "Reject",
    reviewNote: "Review note",
    signedIn: "Signed in as",
    signOut: "Sign out",
    submitted: "Application submitted for administrator review.",
    saved: "Decision saved and the applicant was notified.",
    hotel: "Link Stay",
    merchant: "Link Food",
    delivery_fleet: "Link Delivery fleet",
    taxi_group: "Link Ride group",
  },
  my: {
    apply: "ပါတနာလျှောက်လွှာ",
    applyTitle: "သင့်လုပ်ငန်းကို မှတ်ပုံတင်ပါ",
    applyHelp: "တစ်ကြိမ်သာ ပို့ပါ။ Workspace မဖွင့်မီ TaungGyi Link မှ အချက်အလက်များ စစ်ဆေးမည်။",
    business: "လုပ်ငန်းအမည်",
    contact: "ဆက်သွယ်ရန်ပုဂ္ဂိုလ်",
    phone: "ဖုန်း",
    address: "လုပ်ငန်းလိပ်စာ",
    note: "နောက်ထပ်အချက်အလက် (မဖြစ်မနေမဟုတ်)",
    submit: "စစ်ဆေးရန် ပို့မည်",
    sending: "ပို့နေသည်…",
    pending: "စစ်ဆေးနေဆဲ",
    approved: "အတည်ပြုပြီး",
    rejected: "ငြင်းပယ်ပြီး",
    statusHelp: "သင့်လျှောက်လွှာအခြေအနေ",
    newApplication: "လျှောက်လွှာအသစ် ပို့မည်",
    admin: "ပါတနာ အတည်ပြုမှု",
    adminTitle: "ကိုယ်တိုင်စစ်ဆေးရန် စာရင်း",
    adminHelp: "အတည်မပြုမီ လုပ်ငန်းနှင့် ဖုန်းကို စစ်ဆေးပါ။ ဆုံးဖြတ်ချက်တိုင်း မှတ်တမ်းတင်မည်။",
    signIn: "အက်ဒမင်အကောင့်ဝင်ရန်",
    email: "အီးမေးလ်",
    password: "စကားဝှက်",
    wait: "ခဏစောင့်ပါ…",
    accessDenied: "ဤအကောင့်တွင် အက်ဒမင်ခွင့်ပြုချက် မရှိပါ။",
    empty: "စောင့်ဆိုင်းနေသော လျှောက်လွှာမရှိပါ။",
    approve: "အတည်ပြုပြီး ဖွင့်မည်",
    reject: "ငြင်းပယ်မည်",
    reviewNote: "စစ်ဆေးမှတ်ချက်",
    signedIn: "ဝင်ထားသောအကောင့်",
    signOut: "ထွက်မည်",
    submitted: "အက်ဒမင်စစ်ဆေးရန် လျှောက်လွှာပို့ပြီးပါပြီ။",
    saved: "ဆုံးဖြတ်ချက်သိမ်းပြီး လျှောက်ထားသူကို အသိပေးပြီးပါပြီ။",
    hotel: "ဟိုတယ်",
    merchant: "ဆိုင်",
    delivery_fleet: "ပို့ဆောင်ရေးအဖွဲ့",
    taxi_group: "တက္ကစီအဖွဲ့",
  },
  shn: {
    apply: "ၽိုၼ်ယွၼ်းပၢႆးမၢၵ်ႈမီး",
    applyTitle: "မၢႆၾၢင်ပၢႆးမၢၵ်ႈမီး",
    applyHelp: "သူင်ႇၼိုင်ႈပွၵ်ႈ။ TaungGyi Link တေၵူတ်ႇထတ်းၵွၼ်ႇပိုတ်ႇ workspace။",
    business: "ၸိုဝ်ႈပၢႆးမၢၵ်ႈမီး",
    contact: "ၽူႈၵပ်းသိုပ်ႇ",
    phone: "ၾူၼ်း",
    address: "တီႈယူႇပၢႆးမၢၵ်ႈမီး",
    note: "ၶေႃႈမုၼ်းထႅင်ႈ",
    submit: "သူင်ႇတႃႇၵူတ်ႇထတ်း",
    sending: "တိုၵ်ႉသူင်ႇ…",
    pending: "တိုၵ်ႉၵူတ်ႇထတ်း",
    approved: "ယိုၼ်ယၼ်ယဝ်ႉ",
    rejected: "ပၢၵ်ႈပႅတ်ႈယဝ်ႉ",
    statusHelp: "ငဝ်းလၢႆးၽိုၼ်ယွၼ်း",
    newApplication: "သူင်ႇၽိုၼ်ယွၼ်းမႂ်ႇ",
    admin: "ယိုၼ်ယၼ် partner",
    adminTitle: "သဵၼ်ႈမၢႆၵူတ်ႇထတ်း",
    adminHelp: "ၵူတ်ႇထတ်းပၢႆးမၢၵ်ႈမီးလႄႈၾူၼ်းၵွၼ်ႇယိုၼ်ယၼ်။",
    signIn: "Admin ၶဝ်ႈဢၶွင်ႉ",
    email: "ဢီးမေးလ်",
    password: "မၢႆလပ်ႉ",
    wait: "ပႂ်ႉၵမ်းၼိုင်ႈ…",
    accessDenied: "ဢၶွင်ႉၼႆႉဢမ်ႇမီးသုၼ်ႇ admin။",
    empty: "ပႆႇမီးၽိုၼ်ယွၼ်းဢၼ်ပႂ်ႉဝႆႉ။",
    approve: "ယိုၼ်ယၼ်လႄႈပိုတ်ႇ",
    reject: "ပၢၵ်ႈပႅတ်ႈ",
    reviewNote: "မၢႆတွင်းၵူတ်ႇထတ်း",
    signedIn: "ၶဝ်ႈဝႆႉတင်း",
    signOut: "ဢွၵ်ႇဢၶွင်ႉ",
    submitted: "သူင်ႇၽိုၼ်ယွၼ်းတႃႇ admin ၵူတ်ႇထတ်းယဝ်ႉ။",
    saved: "သိမ်းၶေႃႈတႅပ်းတတ်းလႄႈပွင်ႇၶၢဝ်ႇယဝ်ႉ။",
    hotel: "ႁူင်းလိုဝ်ႈသဝ်း",
    merchant: "ႁၢၼ်ႉ",
    delivery_fleet: "ၸုမ်းသူင်ႇၶူဝ်း",
    taxi_group: "ၸုမ်းတႅၵ်ႇသီႇ",
  },
  zh: {
    apply: "合作伙伴申请",
    applyTitle: "登记您的企业",
    applyHelp: "只需提交一次。TaungGyi Link 会在启用工作区前核实资料。",
    business: "企业名称",
    contact: "联系人",
    phone: "电话",
    address: "营业地址",
    note: "补充信息（可选）",
    submit: "提交审核",
    sending: "正在提交…",
    pending: "审核中",
    approved: "已批准",
    rejected: "已拒绝",
    statusHelp: "您的申请状态",
    newApplication: "提交新申请",
    admin: "合作伙伴审批",
    adminTitle: "人工审批队列",
    adminHelp: "批准前请核实企业和电话号码。所有决定都会记录。",
    signIn: "管理员登录",
    email: "电子邮箱",
    password: "密码",
    wait: "请稍候…",
    accessDenied: "此账户没有管理员权限。",
    empty: "暂无待审核的合作伙伴申请。",
    approve: "批准并启用",
    reject: "拒绝",
    reviewNote: "审核备注",
    signedIn: "当前登录",
    signOut: "退出登录",
    submitted: "申请已提交，等待管理员审核。",
    saved: "决定已保存，并已通知申请人。",
    hotel: "酒店",
    merchant: "商户",
    delivery_fleet: "配送团队",
    taxi_group: "出租车团队",
  },
} as const;
const w = (locale: Locale) => words[locale];

export function AccountSignIn({ locale }: { locale: Locale }) {
  const auth = useAuth();
  const text = w(locale);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useFeedback();
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const data = new FormData(event.currentTarget);
    const error = await auth.signIn(String(data.get("email")), String(data.get("password")));
    setMessage(error ?? "");
    setBusy(false);
  }
  if (auth.loading) return <div className="notice">{text.wait}</div>;
  if (auth.user) return null;
  return (
    <Card className="partner-login">
      <h3>{text.signIn}</h3>
      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        <label className="field">
          <span>{text.email}</span>
          <input name="email" type="email" autoComplete="email" required />
        </label>
        <label className="field">
          <span>{text.password}</span>
          <input name="password" type="password" autoComplete="current-password" required />
        </label>
        <button className="button" disabled={busy}>
          {busy ? text.wait : text.signIn}
        </button>
      </form>
      {message && <p className="form-message">{message}</p>}
    </Card>
  );
}

export function PartnerApplication({ locale, kind }: { locale: Locale; kind: PartnerKind }) {
  const auth = useAuth();
  const text = w(locale);
  const [application, setApplication] = useState<Application | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useFeedback();
  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const { data } = await auth.client
      .from("partner_applications")
      .select(
        "id,kind,business_name,contact_name,contact_phone,address,applicant_note,status,review_note,created_at",
      )
      .eq("applicant_user_id", auth.user.id)
      .eq("kind", kind)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setApplication((data as Application | null) ?? null);
  }, [auth.client, auth.user, kind]);
  useEffect(() => {
    if (!auth.client || !auth.user) return;
    let active = true;
    void auth.client
      .from("partner_applications")
      .select(
        "id,kind,business_name,contact_name,contact_phone,address,applicant_note,status,review_note,created_at",
      )
      .eq("applicant_user_id", auth.user.id)
      .eq("kind", kind)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (active) setApplication((data as Application | null) ?? null);
      });
    return () => {
      active = false;
    };
  }, [auth.client, auth.user, kind]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client || !auth.user) return;
    setBusy(true);
    const data = new FormData(event.currentTarget);
    const { error } = await auth.client.from("partner_applications").insert({
      applicant_user_id: auth.user.id,
      kind,
      business_name: data.get("business_name"),
      contact_name: data.get("contact_name"),
      contact_phone: data.get("contact_phone"),
      address: data.get("address"),
      applicant_note: data.get("note"),
    });
    setMessage(error?.message ?? text.submitted);
    setBusy(false);
    if (!error) await load();
  }
  if (!auth.user) return null;
  const canApply = !application || application.status === "rejected";
  return (
    <section className="partner-section">
      <div className="section-heading">
        <div>
          <h2>{canApply ? text.applyTitle : text.statusHelp}</h2>
        </div>
      </div>
      {application && (
        <Card className="application-status">
          <StatusPill
            tone={
              application.status === "approved"
                ? "success"
                : application.status === "pending"
                  ? "warning"
                  : "neutral"
            }
          >
            {text[application.status]}
          </StatusPill>
          <div>
            <h3>{application.business_name}</h3>
            <p>
              {text[application.kind]} · {application.contact_phone}
            </p>
            {application.review_note && <p>{application.review_note}</p>}
          </div>
        </Card>
      )}
      {canApply && (
        <RecordFormDialog title={text.submit}>
          <form className="form-grid form-grid--two" onSubmit={(event) => void submit(event)}>
            <label className="field">
              <span>{text.business}</span>
              <input name="business_name" minLength={2} required />
            </label>
            <label className="field">
              <span>{text.contact}</span>
              <input name="contact_name" minLength={2} required />
            </label>
            <label className="field">
              <span>{text.phone}</span>
              <input name="contact_phone" type="tel" minLength={6} required />
            </label>
            <label className="field field--wide">
              <span>{text.address}</span>
              <textarea name="address" rows={3} minLength={5} required />
            </label>
            <label className="field field--wide">
              <span>{text.note}</span>
              <textarea name="note" rows={3} maxLength={1000} />
            </label>
            <button className="button field--wide" disabled={busy}>
              {busy ? text.sending : application ? text.newApplication : text.submit}
            </button>
          </form>
          {message && <p className="form-message">{message}</p>}
        </RecordFormDialog>
      )}
    </section>
  );
}

export function AdminPartnerApprovals({
  locale,
  initialKind = "all",
  initialStatus = "pending",
}: {
  locale: Locale;
  initialKind?: PartnerKind | "all";
  initialStatus?: ApplicationStatus | "all";
}) {
  const auth = useAuth();
  const text = w(locale);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [items, setItems] = useState<Application[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [message, setMessage] = useFeedback();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<ApplicationStatus | "all">(initialStatus);
  const [kindFilter, setKindFilter] = useState<PartnerKind | "all">(initialKind);
  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const { data: role } = await auth.client
      .from("platform_user_roles")
      .select("role")
      .eq("user_id", auth.user.id)
      .eq("role", "platform_admin")
      .maybeSingle();
    const allowed = Boolean(role);
    setAuthorized(allowed);
    if (!allowed) return;
    const { data, error } = await auth.client
      .from("partner_applications")
      .select(
        "id,kind,business_name,contact_name,contact_phone,address,applicant_note,status,review_note,created_at",
      )
      .order("created_at");
    if (error) setMessage(error.message);
    else setItems((data ?? []) as Application[]);
  }, [auth.client, auth.user, setMessage]);
  useEffect(() => {
    if (!auth.client || !auth.user) return;
    let active = true;
    void auth.client
      .from("platform_user_roles")
      .select("role")
      .eq("user_id", auth.user.id)
      .eq("role", "platform_admin")
      .maybeSingle()
      .then(async ({ data: role }) => {
        if (!active) return;
        const allowed = Boolean(role);
        setAuthorized(allowed);
        if (!allowed) return;
        const { data, error } = await auth
          .client!.from("partner_applications")
          .select(
            "id,kind,business_name,contact_name,contact_phone,address,applicant_note,status,review_note,created_at",
          )
          .order("created_at");
        if (!active) return;
        if (error) setMessage(error.message);
        else setItems((data ?? []) as Application[]);
      });
    return () => {
      active = false;
    };
  }, [auth.client, auth.user, setMessage]);
  async function decide(id: string, decision: "approved" | "rejected") {
    if (!auth.client) return;
    const { error } = await auth.client.rpc("review_partner_application", {
      application_id: id,
      decision,
      note: notes[id] ?? "",
    });
    setMessage(error?.message ?? text.saved);
    if (!error) await load();
  }
  const filteredItems = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return items.filter(
      (item) =>
        (status === "all" || item.status === status) &&
        (kindFilter === "all" || item.kind === kindFilter) &&
        (!normalized ||
          [item.business_name, item.contact_name, item.contact_phone, item.address].some((value) =>
            value.toLocaleLowerCase().includes(normalized),
          )),
    );
  }, [items, kindFilter, query, status]);
  return (
    <section className="partner-section">
      <AccountSignIn locale={locale} />
      {auth.user && authorized === false && (
        <div className="notice notice--danger">{text.accessDenied}</div>
      )}
      {auth.user && authorized && (
        <div className="approval-list">
          <ManagementListToolbar
            query={query}
            onQueryChange={setQuery}
            searchLabel="Search business, contact, phone, or address"
            resultCount={filteredItems.length}
            filters={[
              {
                label: "Status",
                value: status,
                onChange: (value) => setStatus(value as ApplicationStatus | "all"),
                options: [
                  { value: "all", label: "All statuses" },
                  { value: "pending", label: text.pending },
                  { value: "approved", label: text.approved },
                  { value: "rejected", label: text.rejected },
                ],
              },
              {
                label: "Service",
                value: kindFilter,
                onChange: (value) => setKindFilter(value as PartnerKind | "all"),
                options: [
                  { value: "all", label: "All services" },
                  { value: "hotel", label: text.hotel },
                  { value: "merchant", label: text.merchant },
                  { value: "delivery_fleet", label: text.delivery_fleet },
                  { value: "taxi_group", label: text.taxi_group },
                ],
              },
            ]}
            onClear={() => {
              setQuery("");
              setStatus(initialStatus);
              setKindFilter(initialKind);
            }}
          />
          {filteredItems.length ? (
            <PaginatedList
              items={filteredItems}
              resetKey={`${query}:${status}:${kindFilter}`}
              label="Partner approval pages"
              renderItem={(item) => (
                <DetailCard
                  title={item.business_name}
                  summary={
                    <StatusPill
                      tone={
                        item.status === "approved"
                          ? "success"
                          : item.status === "pending"
                            ? "warning"
                            : "neutral"
                      }
                    >
                      {text[item.status]}
                    </StatusPill>
                  }
                  className="approval-card"
                  key={item.id}
                >
                  <div className="approval-details">
                    <StatusPill
                      tone={
                        item.status === "approved"
                          ? "success"
                          : item.status === "pending"
                            ? "warning"
                            : "neutral"
                      }
                    >
                      {text[item.status]}
                    </StatusPill>
                    <h3>{item.business_name}</h3>
                    <p>
                      <strong>{text[item.kind]}</strong> · {item.contact_name}
                    </p>
                    <a href={`tel:${item.contact_phone}`}>{item.contact_phone}</a>
                    <address>{item.address}</address>
                    {item.applicant_note && <p>{item.applicant_note}</p>}
                  </div>
                  {item.status === "pending" && (
                    <div className="approval-actions">
                      <label className="field">
                        <span>{text.reviewNote}</span>
                        <textarea
                          rows={2}
                          value={notes[item.id] ?? ""}
                          onChange={(event) =>
                            setNotes({ ...notes, [item.id]: event.target.value })
                          }
                        />
                      </label>
                      <div>
                        <button
                          className="button approval-action approval-action--approve"
                          onClick={() => void decide(item.id, "approved")}
                        >
                          <span aria-hidden="true">✓</span>
                          {text.approve}
                        </button>
                        <button
                          className="button button--danger approval-action approval-action--reject"
                          onClick={() => void decide(item.id, "rejected")}
                        >
                          <span aria-hidden="true">×</span>
                          {text.reject}
                        </button>
                      </div>
                    </div>
                  )}
                </DetailCard>
              )}
            />
          ) : (
            <Card className="empty-state">
              <span className="empty-state__icon">✓</span>
              <strong>{text.empty}</strong>
            </Card>
          )}
        </div>
      )}
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

interface DeliveryRequest {
  id: string;
  status: string;
  merchant_order_id: string;
  merchant_orders: { customer_name: string; delivery_address: string; total_minor: number } | null;
}
interface DeliveryFleet {
  id: string;
  display_name: string;
}

export function AdminDeliveryQueue() {
  const auth = useAuth();
  const [requests, setRequests] = useState<DeliveryRequest[]>([]);
  const [fleets, setFleets] = useState<DeliveryFleet[]>([]);
  const [message, setMessage] = useFeedback();
  const [query, setQuery] = useState("");
  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const [{ data: requestData }, { data: fleetData }] = await Promise.all([
      auth.client
        .from("delivery_requests")
        .select("id,status,merchant_order_id")
        .eq("status", "pending")
        .order("created_at"),
      auth.client.from("delivery_fleets").select("id,display_name").eq("is_active", true),
    ]);
    const requestRows = (requestData ?? []) as Omit<DeliveryRequest, "merchant_orders">[];
    const orderIds = requestRows.map((request) => request.merchant_order_id);
    const orderResult = orderIds.length
      ? await auth.client
          .from("merchant_orders")
          .select("id,customer_name,delivery_address,total_minor")
          .in("id", orderIds)
      : { data: [], error: null };
    const orders = (orderResult.data ?? []) as Array<{
      id: string;
      customer_name: string;
      delivery_address: string;
      total_minor: number;
    }>;
    setRequests(
      requestRows.map((request) => ({
        ...request,
        merchant_orders: orders.find((order) => order.id === request.merchant_order_id) ?? null,
      })),
    );
    setFleets((fleetData ?? []) as DeliveryFleet[]);
  }, [auth.client, auth.user]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  async function assign(id: string, fleetId: string) {
    if (!auth.client || !fleetId) return;
    const { error } = await auth.client.rpc("admin_assign_delivery_request", {
      request_id: id,
      target_fleet_id: fleetId,
    });
    setMessage(error?.message ?? "Delivery job created and sent to the fleet queue.");
    if (!error) await load();
  }
  if (!auth.user) return null;
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredRequests = requests.filter((request) =>
    [request.merchant_orders?.customer_name, request.merchant_orders?.delivery_address].some(
      (value) => value?.toLocaleLowerCase().includes(normalizedQuery),
    ),
  );
  return (
    <section className="partner-section">
      <div className="section-heading">
        <div>
          <h2>Orders waiting for a fleet</h2>
        </div>
      </div>
      <ManagementListToolbar
        query={query}
        onQueryChange={setQuery}
        searchLabel="Search customer or delivery address"
        resultCount={filteredRequests.length}
        onClear={() => setQuery("")}
      />
      <div className="request-list">
        {filteredRequests.length ? (
          <PaginatedList
            items={filteredRequests}
            resetKey={query}
            label="Delivery request pages"
            renderItem={(request) => (
              <Card className="request-card" key={request.id}>
                <div>
                  <StatusPill tone="warning">Waiting</StatusPill>
                  <h3>{request.merchant_orders?.customer_name ?? "Customer order"}</h3>
                  <p>{request.merchant_orders?.delivery_address}</p>
                  <p>
                    {((request.merchant_orders?.total_minor ?? 0) / 100).toLocaleString()} MMK cash
                    to collect
                  </p>
                </div>
                <select
                  defaultValue=""
                  aria-label="Assign fleet"
                  onChange={(event) => void assign(request.id, event.target.value)}
                >
                  <option value="" disabled>
                    Assign fleet
                  </option>
                  {fleets.map((fleet) => (
                    <option key={fleet.id} value={fleet.id}>
                      {fleet.display_name}
                    </option>
                  ))}
                </select>
              </Card>
            )}
          />
        ) : (
          <Card className="empty-state">
            <span className="empty-state__icon">✓</span>
            <strong>No delivery requests waiting.</strong>
          </Card>
        )}
      </div>
      {message && (
        <p className="form-message" role="status">
          {message}
        </p>
      )}
    </section>
  );
}

interface OperationalMetric {
  label: string;
  count: number;
  tone: "neutral" | "warning";
}
export function AdminOperationsOverview() {
  const auth = useAuth();
  const [metrics, setMetrics] = useState<OperationalMetric[]>([]);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const queries = await Promise.all([
      auth.client
        .from("partner_applications")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      auth.client
        .from("hotel_booking_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      auth.client
        .from("merchant_orders")
        .select("id", { count: "exact", head: true })
        .in("status", ["pending", "accepted", "preparing"]),
      auth.client
        .from("delivery_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      auth.client
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .is("read_at", null),
    ]);
    const labels = [
      "Partner approvals",
      "Link Stay requests",
      "Link Food orders",
      "Link Delivery assignments",
      "Unread notifications",
    ];
    setMetrics(
      labels.map((label, index) => ({
        label,
        count: queries[index]?.count ?? 0,
        tone: (queries[index]?.count ?? 0) > 0 ? "warning" : "neutral",
      })),
    );
    setUpdatedAt(new Date());
  }, [auth.client, auth.user]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    const interval = window.setInterval(() => void load(), 60_000);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
    };
  }, [load]);
  if (!auth.user) return null;
  return (
    <section className="operations-overview">
      <div className="section-heading">
        <div>
          <h2>What needs attention now</h2>
        </div>
        <button className="button button--secondary" onClick={() => void load()}>
          Refresh
        </button>
      </div>
      <div className="operations-grid">
        {metrics.map((metric) => (
          <Card className="metric-card" key={metric.label}>
            <StatusPill tone={metric.tone}>
              {metric.count > 0 ? "Action needed" : "Clear"}
            </StatusPill>
            <strong>{metric.count}</strong>
            <span>{metric.label}</span>
          </Card>
        ))}
      </div>
      {updatedAt && <p className="dashboard-updated">Updated {updatedAt.toLocaleTimeString()}</p>}
    </section>
  );
}

type AvailabilityResourceType =
  "hotel" | "merchant" | "delivery_fleet" | "taxi_group" | "taxi_driver";

interface AvailabilityResource {
  id: string;
  type: AvailabilityResourceType;
  name: string;
  enabled: boolean;
  organization_name: string;
  created_at: string;
  updated_at: string;
  created_by_name: string;
  created_by_email: string;
  updated_by_name: string;
  updated_by_email: string;
}

const availabilityLabels: Record<AvailabilityResourceType, string> = {
  hotel: "Link Stay",
  merchant: "Link Food",
  delivery_fleet: "Link Delivery fleet",
  taxi_group: "Link Ride group",
  taxi_driver: "Link Ride driver",
};

export function AdminPublicationControls() {
  const auth = useAuth();
  const [resources, setResources] = useState<AvailabilityResource[]>([]);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [busyId, setBusyId] = useState("");
  const [message, setMessage] = useFeedback();
  const [query, setQuery] = useState("");
  const [visibility, setVisibility] = useState<"all" | "visible" | "hidden">("all");
  const [typeFilter, setTypeFilter] = useState<AvailabilityResourceType | "all">("all");
  const [expandedId, setExpandedId] = useState("");

  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    setMessage("");
    const { data: role, error: roleError } = await auth.client
      .from("platform_user_roles")
      .select("role")
      .eq("user_id", auth.user.id)
      .eq("role", "platform_admin")
      .maybeSingle();
    if (roleError) {
      setAuthorized(false);
      setMessage(roleError.message);
      return;
    }
    const allowed = Boolean(role);
    setAuthorized(allowed);
    if (!allowed) return;

    const { data, error } = await auth.client.rpc("admin_partner_resource_records");
    if (error) {
      setMessage(error.message);
      return;
    }
    setResources(
      ((data as AvailabilityResource[] | null) ?? []).sort((left, right) =>
        `${left.type}-${left.name}`.localeCompare(`${right.type}-${right.name}`),
      ),
    );
  }, [auth.client, auth.user, setMessage]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  async function toggle(resource: AvailabilityResource) {
    if (!auth.client) return;
    setBusyId(resource.id);
    setMessage("");
    const { error } = await auth.client.rpc("admin_set_partner_availability", {
      resource_type: resource.type,
      resource_id: resource.id,
      enabled: !resource.enabled,
    });
    setBusyId("");
    if (error) {
      setMessage(error.message);
      return;
    }
    setMessage(`${resource.name} was ${resource.enabled ? "disabled" : "enabled"}.`);
    await load();
  }

  const filteredResources = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return resources.filter(
      (resource) =>
        (visibility === "all" || resource.enabled === (visibility === "visible")) &&
        (typeFilter === "all" || resource.type === typeFilter) &&
        (!normalized || resource.name.toLocaleLowerCase().includes(normalized)),
    );
  }, [query, resources, typeFilter, visibility]);

  if (!auth.user || authorized === false) return null;
  return (
    <section className="publication-controls">
      <div className="section-heading">
        <div>
          <h2>Choose what customers can access</h2>
        </div>
      </div>
      {message && <p className="form-message">{message}</p>}
      {authorized && (
        <div className="publication-list">
          <ManagementListToolbar
            query={query}
            onQueryChange={setQuery}
            searchLabel="Search partner profiles"
            resultCount={filteredResources.length}
            filters={[
              {
                label: "Visibility",
                value: visibility,
                onChange: (value) => setVisibility(value as typeof visibility),
                options: [
                  { value: "all", label: "All visibility" },
                  { value: "visible", label: "Customer visible" },
                  { value: "hidden", label: "Not visible" },
                ],
              },
              {
                label: "Service",
                value: typeFilter,
                onChange: (value) => setTypeFilter(value as typeof typeFilter),
                options: [
                  { value: "all", label: "All services" },
                  ...Object.entries(availabilityLabels).map(([value, label]) => ({ value, label })),
                ],
              },
            ]}
            onClear={() => {
              setQuery("");
              setVisibility("all");
              setTypeFilter("all");
            }}
          />
          {filteredResources.length ? (
            <PaginatedList
              items={filteredResources}
              resetKey={`${query}:${visibility}:${typeFilter}`}
              label="Publication record pages"
              renderItem={(resource) => (
                <Card className="publication-row" key={`${resource.type}-${resource.id}`}>
                  <div className="publication-row__main">
                    <StatusPill tone={resource.enabled ? "success" : "neutral"}>
                      {resource.enabled ? "Customer visible" : "Not visible"}
                    </StatusPill>
                    <h3>{resource.name}</h3>
                    <p>{availabilityLabels[resource.type]}</p>
                    {expandedId === resource.id && (
                      <dl className="publication-details">
                        <div>
                          <dt>Owner</dt>
                          <dd>{resource.organization_name}</dd>
                        </div>
                        <div>
                          <dt>Created by</dt>
                          <dd>
                            {resource.created_by_name ||
                              resource.created_by_email ||
                              "System provisioned"}
                          </dd>
                        </div>
                        <div>
                          <dt>Created</dt>
                          <dd>{formatRecordDate(resource.created_at, "en", true)}</dd>
                        </div>
                        <div>
                          <dt>Last updated by</dt>
                          <dd>
                            {resource.updated_by_name || resource.updated_by_email || "System"}
                          </dd>
                        </div>
                        <div>
                          <dt>Last updated</dt>
                          <dd>{formatRecordDate(resource.updated_at, "en", true)}</dd>
                        </div>
                      </dl>
                    )}
                  </div>
                  <div className="publication-row__actions">
                    <button
                      className="action-capsule action-capsule--detail"
                      type="button"
                      aria-expanded={expandedId === resource.id}
                      onClick={() => setExpandedId(expandedId === resource.id ? "" : resource.id)}
                    >
                      {expandedId === resource.id ? "Hide details" : "View details"}
                    </button>
                    <button
                      className={`action-capsule ${resource.enabled ? "action-capsule--disable" : "action-capsule--enable"}`}
                      disabled={busyId === resource.id}
                      onClick={() => void toggle(resource)}
                    >
                      {busyId === resource.id
                        ? "Saving…"
                        : resource.enabled
                          ? "Disable"
                          : resource.type === "taxi_driver"
                            ? "Verify driver"
                            : "Enable"}
                    </button>
                  </div>
                </Card>
              )}
            />
          ) : (
            <Card className="empty-state">
              <span className="empty-state__icon">✓</span>
              <strong>No partner profiles are waiting for publication.</strong>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}
